import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Input, notification } from "antd";
import {
   CheckOutlined,
   FileTextOutlined,
   LinkOutlined,
   RightOutlined,
   SearchOutlined
} from "@ant-design/icons";
import { groupByCategory, searchItems } from "./helpSearch";
import { articleSearchFields, renderArticle } from "./guideHelpers";
import { buildHelpLink } from "./helpTriggers";
import ArticleFeedback from "./ArticleFeedback";

/**
 * Click handler for rendered markdown. Links work three ways: `#article-id`
 * opens another article here, `/route` moves the app to that page, and full
 * urls open in a new tab.
 */
export const useGuideLinks = ({ articles, onOpen, onClose }) => {
   const navigate = useNavigate();

   return useCallback(
      (event) => {
         const link = event.target.closest("a");
         const href = link?.getAttribute("href");
         if (!href) return;

         event.preventDefault();
         if (href.startsWith("#")) {
            const id = href.slice(1);
            if (articles.some((item) => item.id === id)) onOpen(id);
         } else if (href.startsWith("/")) {
            onClose();
            navigate(href);
         } else {
            window.open(href, "_blank", "noopener,noreferrer");
         }
      },
      [articles, navigate, onClose, onOpen]
   );
};

const CopyArticleLink = ({ articleId }) => {
   const [copied, setCopied] = useState(false);

   useEffect(() => {
      if (!copied) return undefined;
      const timer = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(timer);
   }, [copied]);

   const copy = async () => {
      try {
         await navigator.clipboard.writeText(buildHelpLink(articleId));
         setCopied(true);
      } catch (error) {
         notification.error({
            message: "Oops! A problem has occurred!",
            description: "We couldn't copy the link, please try again."
         });
      }
   };

   return (
      <Button
         type="text"
         size="small"
         className="help-article__copy"
         icon={copied ? <CheckOutlined /> : <LinkOutlined />}
         onClick={copy}
      >
         {copied ? "Link copied" : "Copy link"}
      </Button>
   );
};

export const ArticleLink = ({ article, onOpen }) => (
   <button type="button" className="help-guide-link" onClick={() => onOpen(article.id)}>
      <FileTextOutlined className="help-guide-link__icon" />
      <span className="help-guide-link__text">
         <span className="help-guide-link__title">{article.title}</span>
         {article.summary && <span className="help-guide-link__summary">{article.summary}</span>}
      </span>
      <RightOutlined className="help-guide-link__arrow" />
   </button>
);

/** Drawer view listing the User Guide articles, grouped by category. */
export const UserGuideList = ({ articles, onOpen }) => {
   const [query, setQuery] = useState("");
   const groups = useMemo(
      () => groupByCategory(searchItems(articles, query, articleSearchFields)),
      [articles, query]
   );

   return (
      <>
         <Input
            allowClear
            className="help-center__search"
            placeholder="Search the guide"
            prefix={<SearchOutlined />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
         />

         {groups.length === 0 ? (
            <p className="help-center__no-results">No articles match “{query.trim()}”.</p>
         ) : (
            groups.map((group) => (
               <section key={group.category || "uncategorized"} className="help-center__section">
                  {group.category && (
                     <h5 className="help-center__section-title">{group.category}</h5>
                  )}
                  <div className="help-center__articles">
                     {group.items.map((article) => (
                        <ArticleLink key={article.id} article={article} onOpen={onOpen} />
                     ))}
                  </div>
               </section>
            ))
         )}
      </>
   );
};

/** One article, with a link to share it and a "Was this helpful?" vote. */
export const UserGuideArticle = ({ article, articles, onOpen, onClose, canOpenTickets }) => {
   const ref = useRef(null);
   const handleClick = useGuideLinks({ articles, onOpen, onClose });
   const html = useMemo(() => renderArticle(article.body), [article.body]);
   const related = (article.related || [])
      .map((id) => articles.find((item) => item.id === id))
      .filter(Boolean);

   useEffect(() => {
      ref.current?.closest(".ant-drawer-body")?.scrollTo({ top: 0 });
   }, [article.id]);

   return (
      <article ref={ref} className="help-article">
         <div className="help-article__header">
            {article.category && <span className="help-article__category">{article.category}</span>}
            <CopyArticleLink articleId={article.id} />
         </div>
         <h2 className="help-article__title">{article.title}</h2>
         {article.summary && <p className="help-article__summary">{article.summary}</p>}

         {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
         <div
            className="help-article__body"
            onClick={handleClick}
            dangerouslySetInnerHTML={{ __html: html }}
         />

         <ArticleFeedback key={article.id} articleId={article.id} />

         {related.length > 0 && (
            <section className="help-article__related">
               <h5 className="help-center__section-title">Related articles</h5>
               <div className="help-center__articles">
                  {related.map((item) => (
                     <ArticleLink key={item.id} article={item} onOpen={onOpen} />
                  ))}
               </div>
            </section>
         )}

         {canOpenTickets && (
            <p className="help-article__footer">
               Still need help?{" "}
               <Link to="/tickets" onClick={onClose}>
                  Open a support ticket
               </Link>
            </p>
         )}
      </article>
   );
};
